import {
  inject,
  Injectable,
  resource,
  ResourceRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  IOnDemandBookingRequest,
  IOnDemandBookingResponse,
  MILLISECONDS_PER_MINUTE,
  RESERVATION_DURATION_MINUTES,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { OnDemandAvailabilityGateway } from '../api/on-demand/on-demand-availability.gateway';
import { OnDemandSelectionStateService } from './on-demand-selection-state.service';
import { OnDemandAvailabilityStore } from '../api/on-demand/on-demand-availability-store.service';
import { LoggingService } from '../logging/logging.service';

@Injectable({ providedIn: 'root' })
export class OnDemandBookingStore {
  private readonly gateway: OnDemandAvailabilityGateway =
    new OnDemandAvailabilityGateway();
  private readonly selectionState: OnDemandSelectionStateService = inject(
    OnDemandSelectionStateService
  );
  private readonly availabilityStore: OnDemandAvailabilityStore = inject(
    OnDemandAvailabilityStore
  );

  constructor(private readonly log: LoggingService) {}

  private readonly reserveTrigger: WritableSignal<
    IOnDemandBookingRequest | undefined
  > = signal(undefined);

  private readonly reserveResource: ResourceRef<
    IApiResponse<IOnDemandBookingResponse> | undefined
  > = resource({
    params: this.reserveTrigger,
    loader: async ({ params }) => {
      if (!params) return undefined;

      const response = await firstValueFrom(this.gateway.reserve(params));

      if (response?.data) {
        this.selectionState.reservationBookingResponse.set(response.data);

        const expires = new Date(
          Date.now() + RESERVATION_DURATION_MINUTES * MILLISECONDS_PER_MINUTE
        );
        this.selectionState.reservationExpiresAt.set(expires.toISOString());
      }
      return response;
    },
  });

  triggerReservation(
    passengerCount: number,
    customerName: string,
    customerEmail: string,
    customerPhone?: string
  ): void {
    const operatorUrl =
      this.availabilityStore.matchedMapping()?.url ??
      this.selectionState.checkoutOperatorMapping()?.url ??
      this.selectionState.reservationOperatorUrl();
    const option = this.selectionState.selectedConnectionOption();

    if (!operatorUrl || !option) {
      this.log.warn(
        'OnDemandBookingStore: Missing operator URL or connection option.'
      );
      return;
    }

    this.selectionState.reservationOperatorUrl.set(operatorUrl);
    this.reserveTrigger.set({
      operatorUrl,
      publicTransportXml: option.publicTransportXml,
      earliestDeparture: option.departureTimetabled,
      latestArrival: option.arrivalTimetabled,
      passengerCount,
      isReservation: true,
      extrasPerPassenger: this.selectionState.extrasPerPassenger(),
      customerName,
      customerEmail,
      customerPhone,
    });
  }

  getConfirmationRequest(
    customerName: string,
    customerEmail: string,
    customerPhone?: string
  ): IOnDemandBookingRequest {
    const operatorUrl =
      this.availabilityStore.matchedMapping()?.url ??
      this.selectionState.reservationOperatorUrl();
    const reservation =
      this.selectionState.reservationBookingResponse() ??
      this.reserveResource.value()?.data;

    if (!operatorUrl || !reservation) {
      throw new Error('No on-demand reservation is available to confirm.');
    }
    return {
      operatorUrl,
      publicTransportXml: '',
      earliestDeparture: '',
      passengerCount: this.selectionState.passengerCount(),
      isReservation: false,
      systemRef: reservation.systemRef,
      bookingCode: reservation.bookingRef,
      customerName,
      customerEmail,
      customerPhone,
    };
  }
}
